import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { editScheduleStop, getScheduleDetail_RCB01 } from "src/api/billingSchedule/billingSchedule-api";
import { useApiOperation } from "src/api/hooks";
import { BaseButton, BaseDatePicker, BaseModal, BaseTextarea } from "src/components";

type Props = {
  showBtn: boolean;
  round?: string;
  totalRound: any;
  isOpen: boolean;
  scheduleId: string;
  setIsOpen: any;
  onClose?: React.MouseEventHandler<HTMLButtonElement>;
  onClick: any;
};

const StopModal = (props: Props) => {
  const params = useParams();
  const { executeAsync: scheduleStop } = useApiOperation(editScheduleStop);
  const { executeAsync: getScheduleList } = useApiOperation(getScheduleDetail_RCB01);

  // 폼 라이브러리 제거: 필수 검증 대상(선택 회차)이 사라져 검증이 불필요해졌으므로
  // 편집 필드(중지 사유)와 기록용 값(중지 일시)만 로컬 state로 관리한다.
  const [reason, setReason] = useState("");
  const [stopDate, setStopDate] = useState<Date | null>(null);
  const [isSave, setIsSave] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [stopObj, setStopObj] = useState<any>();

  const checkStop = useCallback(async () => {
    const res: any = await getScheduleList({ contractId: Number(params.id), supplyType: "RENTAL" });

    const schedule = res.data.data.scheduleList[0];

    // 이미 중지된 건이면 기존 기록을 보여주고, 아니면 현재 시점으로 고정한다.
    if (String(schedule.isContractCancel) === "true") {
      setReason(schedule.contractCancelReason ?? "");
      setStopDate(schedule.contractCancelDate ? moment(schedule.contractCancelDate).toDate() : new Date());
    } else {
      setReason("");
      setStopDate(new Date());
    }
    setStopObj(schedule);
  }, [getScheduleList, params.id]);

  const updateScheduleStop = useCallback(async () => {
    // 상세 조회(checkStop)가 끝나기 전이면 stopObj가 없어 scheduleId 참조 시 에러가 나므로 방어
    if (!stopObj) {
      setIsSave(false);
      return;
    }

    // 선택 회차: 빌링스케줄 정책 변경(2024)으로 더 이상 유효하지 않은 값이라 화면에서 숨기고 0으로 고정 전송한다.
    // 중지 일시: 저장 즉시 스케줄이 중지되므로 현재 시점을 기록용 값으로 고정한다.
    const schedule: any = {
      isContractCancel: true,
      contractCancelStartOrder: 0,
      contractCancelDate: stopDate ?? new Date(),
    };
    if (reason !== "") {
      schedule.contractCancelReason = reason;
    }

    const merged = { ...stopObj, ...schedule };
    setStopObj(merged);

    try {
      await scheduleStop({ scheduleId: stopObj.scheduleId, schedule });
      props.onClick(merged, merged);
      setIsSuccess(true);
    } finally {
      setIsSave(false);
    }
  }, [props, scheduleStop, stopObj, stopDate, reason]);

  useEffect(() => {
    if (props.isOpen) {
      checkStop();
    }
  }, [props.isOpen, checkStop]);

  return (
    <div>
      <>
        <BaseModal className="dialog-modal  small-size" isOpen={props.isOpen}>
          <>
            <div className="modal-title border-bottom">
              <div className="left-area align-items-center">
                <span>빌링스케줄 중지</span>
                <p className="sub-title-info ml40">계약자와 협의하신 내용을 입력해 주세요. </p>
              </div>
              <div className="right-area"></div>
            </div>
            <div className="text-left px30 pt20 pb100">
              <section className="flex-center-start mb20">
                <div className="minmax140">
                  <p className="text-gray600">중지 일시</p>
                </div>
                <div className="minmax300">
                  {/* 중지 일시는 스케줄 중지에 사용되지 않는 기록용 값이므로 현재 시점으로 고정하고 선택은 막는다. */}
                  <BaseDatePicker
                    timeIntervals={10}
                    type="date"
                    selectedDate={stopDate}
                    setDate={() => {}}
                    name="contractCancelDate"
                    disabled={true}
                  />
                </div>
              </section>
              <section className="flex-center-start">
                <div className="minmax140">
                  <p className="text-gray600">중지 사유</p>
                </div>
                <div className="minmax300">
                  <BaseTextarea
                    placeholder="중지사유를 입력해주세요"
                    className="mr8"
                    onChange={(value: string) => setReason(value)}
                    value={reason}
                    name="contractCancelReason"
                    maxLength={1000}
                    disabled={!props.showBtn}
                  />
                </div>
              </section>
            </div>
            <div className="base-modal__btn-wrap">
              {props.showBtn === true ? (
                <>
                  <BaseButton title={"취소"} onClick={props.onClose} className="color-white no-outline text-gray600 mr10" />

                  <BaseButton title="저장" onClick={() => setIsSave(true)} disabled={!stopObj} />
                </>
              ) : (
                <>
                  <BaseButton title={"확인"} onClick={props.onClose} className="no-outline mr10" />
                </>
              )}
            </div>
          </>
        </BaseModal>
        <BaseModal
          isOpen={isSave}
          btnLeftTitle="취소"
          btnRightTitle="확인"
          onClose={() => setIsSave(false)}
          onClick={() => updateScheduleStop()}
          title={"저장하시겠습니까?"}
        ></BaseModal>
        <BaseModal isOpen={isSuccess} btnRightTitle="확인" onClick={() => setIsSuccess(false)} title={"저장되었습니다."}></BaseModal>
      </>
    </div>
  );
};

export default StopModal;
